import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import toast from "react-hot-toast";
import {
  Search,
  Filter,
  Download,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  FileSpreadsheet,
  FileText,
  Printer,
  RotateCcw,
  LayoutDashboard,
  CheckCircle2,
  Clock,
  AlertCircle
} from "lucide-react";
import { REPORT_TYPES } from "./AdminReportsDashboard";

// Sample rich dataset tailored for sub-reports
const SAMPLE_SUB_REPORTS_DATA = {
  sales: [
    { id: "SL-9081", date: "07 Oct 2026", orderId: "ORD-2026-001245", customer: "Priya Sharma", items: 3, gross: 4200, discount: 200, tax: 480, net: 4480, payment: "UPI", status: "Completed" },
    { id: "SL-9082", date: "07 Oct 2026", orderId: "ORD-2026-001244", customer: "Rahul Mehta", items: 2, gross: 1800, discount: 0, tax: 216, net: 2016, payment: "Credit Card", status: "Completed" },
    { id: "SL-9083", date: "06 Oct 2026", orderId: "ORD-2026-001243", customer: "Anita Desai", items: 1, gross: 3250, discount: 150, tax: 372, net: 3472, payment: "Net Banking", status: "Completed" },
    { id: "SL-9084", date: "06 Oct 2026", orderId: "ORD-2026-001242", customer: "Vikram Singh", items: 4, gross: 5600, discount: 300, tax: 636, net: 5936, payment: "COD", status: "Completed" },
    { id: "SL-9085", date: "05 Oct 2026", orderId: "ORD-2026-001241", customer: "Neha Kapoor", items: 2, gross: 2490, discount: 100, tax: 286, net: 2676, payment: "UPI", status: "Completed" },
  ],
  orders: [
    { id: "ORD-2026-001245", date: "07 Oct 2026", customer: "Priya Sharma", city: "Bhubaneswar", items: 3, total: 4480, payment: "Paid (UPI)", fulfillment: "Delivered", courier: "BlueDart" },
    { id: "ORD-2026-001244", date: "07 Oct 2026", customer: "Rahul Mehta", city: "Mumbai", items: 2, total: 2016, payment: "Paid (Card)", fulfillment: "Shipped", courier: "Delhivery" },
    { id: "ORD-2026-001243", date: "06 Oct 2026", customer: "Anita Desai", city: "Bengaluru", items: 1, total: 3472, payment: "Paid (NetBank)", fulfillment: "Processing", courier: "India Post" },
    { id: "ORD-2026-001242", date: "06 Oct 2026", customer: "Vikram Singh", city: "Jaipur", items: 4, total: 5936, payment: "Pending (COD)", fulfillment: "Pending", courier: "DTDC" },
    { id: "ORD-2026-001241", date: "05 Oct 2026", customer: "Neha Kapoor", city: "Delhi", items: 2, total: 2676, payment: "Paid (UPI)", fulfillment: "Delivered", courier: "BlueDart" },
  ],
  products: [
    { sku: "BK-SAR-001", name: "Handwoven Cotton Saree", category: "Handloom", stock: 142, sold: 245, unitPrice: 2499, totalRevenue: 612105, rating: "4.9 (128)" },
    { sku: "BK-TER-042", name: "Terracotta Diya Set (6 pcs)", category: "Pottery", stock: 85, sold: 189, unitPrice: 1600, totalRevenue: 302511, rating: "4.8 (94)" },
    { sku: "BK-WOO-019", name: "Wooden Wall Art", category: "Wood Crafts", stock: 34, sold: 152, unitPrice: 1250, totalRevenue: 190000, rating: "4.7 (65)" },
    { sku: "BK-IKA-088", name: "Ikat Dupatta", category: "Textiles", stock: 67, sold: 128, unitPrice: 1300, totalRevenue: 166400, rating: "4.9 (82)" },
    { sku: "BK-BRS-005", name: "Brass Pooja Diya Set", category: "Metal Crafts", stock: 41, sold: 96, unitPrice: 1499, totalRevenue: 143904, rating: "4.8 (51)" },
  ],
  sellers: [
    { id: "SLR-101", name: "Odisha Handlooms", region: "Odisha", products: 64, orders: 45, grossSales: 125680, commission: 12568, payoutStatus: "Settled" },
    { id: "SLR-102", name: "Rajasthan Crafts", region: "Rajasthan", products: 48, orders: 38, grossSales: 98450, commission: 9845, payoutStatus: "Processing" },
    { id: "SLR-103", name: "Assam Weaves", region: "Assam", products: 39, orders: 32, grossSales: 76230, commission: 7623, payoutStatus: "Settled" },
    { id: "SLR-104", name: "Tribal Artisans", region: "Jharkhand", products: 29, orders: 28, grossSales: 65440, commission: 6544, payoutStatus: "Pending" },
    { id: "SLR-105", name: "North East Crafts", region: "Meghalaya", products: 22, orders: 24, grossSales: 52190, commission: 5219, payoutStatus: "Settled" },
  ],
  customers: [
    { id: "CUST-501", name: "Priya Sharma", email: "priya.s@example.com", phone: "+91 98765 43210", orders: 12, spent: 24560, lastOrder: "07 Oct 2026", segment: "VIP Artisan" },
    { id: "CUST-502", name: "Rahul Mehta", email: "rahul.m@example.com", phone: "+91 98234 11223", orders: 9, spent: 18790, lastOrder: "07 Oct 2026", segment: "Regular" },
    { id: "CUST-503", name: "Anita Desai", email: "anita.d@example.com", phone: "+91 99100 88776", orders: 8, spent: 16320, lastOrder: "06 Oct 2026", segment: "Regular" },
    { id: "CUST-504", name: "Vikram Singh", email: "vikram.s@example.com", phone: "+91 97654 33211", orders: 7, spent: 12450, lastOrder: "06 Oct 2026", segment: "Occasional" },
    { id: "CUST-505", name: "Neha Kapoor", email: "neha.k@example.com", phone: "+91 98112 44332", orders: 6, spent: 10890, lastOrder: "05 Oct 2026", segment: "Occasional" },
  ],
  tax: [
    { hsn: "5208", description: "Handwoven Cotton Fabrics", taxableVal: 850000, cgst: "2.5% (₹ 21,250)", sgst: "2.5% (₹ 21,250)", igst: "0% (₹ 0)", totalTax: 42500 },
    { hsn: "6912", description: "Ceramic & Terracotta Articles", taxableVal: 480000, cgst: "6.0% (₹ 28,800)", sgst: "6.0% (₹ 28,800)", igst: "0% (₹ 0)", totalTax: 57600 },
    { hsn: "4420", description: "Woodcraft Statuettes & Boxes", taxableVal: 320000, cgst: "6.0% (₹ 19,200)", sgst: "6.0% (₹ 19,200)", igst: "0% (₹ 0)", totalTax: 38400 },
    { hsn: "7419", description: "Brass Decorative Lamps/Diyas", taxableVal: 240000, cgst: "9.0% (₹ 21,600)", sgst: "9.0% (₹ 21,600)", igst: "0% (₹ 0)", totalTax: 43200 },
  ],
  refunds: [
    { id: "REF-201", date: "06 Oct 2026", orderId: "ORD-2026-001198", customer: "Suman Roy", amount: 1499, reason: "Size mismatch", status: "Refunded via UPI", time: "24 hrs" },
    { id: "REF-202", date: "05 Oct 2026", orderId: "ORD-2026-001185", customer: "Alok Gupta", amount: 850, reason: "Damaged in transit", status: "Refunded to Card", time: "48 hrs" },
    { id: "REF-203", date: "04 Oct 2026", orderId: "ORD-2026-001170", customer: "Pooja Hegde", amount: 2300, reason: "Customer cancelled", status: "Completed", time: "12 hrs" },
  ]
};

const ReportDetailView = ({ type }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const currentMeta = REPORT_TYPES.find((r) => r.id === type) || {
    label: "Reports Dashboard",
    path: "/admin-reports",
  };

  const rows = SAMPLE_SUB_REPORTS_DATA[type] || SAMPLE_SUB_REPORTS_DATA.sales;

  // Search filter
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter((r) =>
      Object.values(r).some((val) => String(val).toLowerCase().includes(q))
    );
  }, [rows, searchTerm]);

  // Export handlers
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, currentMeta.label);
    XLSX.writeFile(wb, `Bharat_Karigari_${type}_report.xlsx`);
    toast.success("Excel sheet downloaded successfully");
  };

  const handleExportCSV = () => {
    if (!filteredRows.length) return;
    const headers = Object.keys(filteredRows[0]);
    let csv = headers.join(",") + "\n";
    filteredRows.forEach((r) => {
      csv += headers.map((h) => `"${r[h] || ""}"`).join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Bharat_Karigari_${type}_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file downloaded successfully");
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text(`Bharat Karigari - ${currentMeta.label}`, 14, 20);
    if (filteredRows.length > 0) {
      const headers = Object.keys(filteredRows[0]);
      const body = filteredRows.map((r) => headers.map((h) => r[h]));
      doc.autoTable({
        startY: 28,
        head: [headers],
        body: body,
        headStyles: { fillColor: [176, 125, 23] },
      });
    }
    doc.save(`Bharat_Karigari_${type}_report.pdf`);
    toast.success("PDF generated successfully");
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 shadow-xs p-4 sm:p-5 mt-4">
      {/* Header bar of the report */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-slate-900 tracking-tight">
              {currentMeta.label} Detailed Records
            </h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-[#b07d17] font-semibold border border-amber-200">
              {filteredRows.length} Rows
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            Full transactional log matching currently applied date and status filters
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => navigate("/admin-reports")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <LayoutDashboard size={14} className="text-[#b07d17]" />
            <span>Overview Dashboard</span>
          </button>

          <button
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors"
          >
            <FileSpreadsheet size={14} />
            <span>Excel</span>
          </button>

          <button
            onClick={handleExportPDF}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-800 hover:bg-rose-100 transition-colors"
          >
            <FileText size={14} />
            <span>PDF</span>
          </button>

          <button
            onClick={handleExportCSV}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
          >
            <Download size={14} />
            <span>CSV</span>
          </button>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative mb-3 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder={`Search ${currentMeta.label}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#b07d17]"
        />
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto border border-slate-100 rounded-lg">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
              {filteredRows.length > 0 &&
                Object.keys(filteredRows[0]).map((col) => (
                  <th key={col} className="px-3.5 py-2.5 whitespace-nowrap">
                    {col.replace(/([A-Z])/g, " $1").toUpperCase()}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredRows.map((row, idx) => (
              <tr key={idx} className="hover:bg-slate-50/70 transition-colors">
                {Object.values(row).map((val, cellIdx) => (
                  <td key={cellIdx} className="px-3.5 py-2.5 whitespace-nowrap text-slate-700">
                    {typeof val === "number" ? val.toLocaleString("en-IN") : String(val)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportDetailView;
